import React from 'react';

import {
  chemicalBaseTypes,
  seasonTypes,
  systemTypes,
  userLevels,
  chemicalBaseTypeNames,
  countries,
  optimizerProducts,
} from './constants.jsx';

import {getIdealRange as getWardenIdealRange} from './idealRanges';

export function addCommas(num) {
  return (num + '').split('').reduce((acc, x, i, arr) => (arr.length - 1 - i) % 3 === 0 && i !== arr.length - 1 ? acc + x + ',' : acc + x);
}


export const poolOptionsTags = {
  finish: {
    B: 'general.finish.pebbled',
    C: 'general.finish.plaster.colored',
    F: 'general.finish.fiberglass',
    P: 'general.finish.painted',
    S: 'general.finish.stainless-steel',
    U: 'general.finish.vinyl.unprinted',
    V: 'general.finish.vinyl.printed',
    W: 'general.finish.plaster.white',
  },
  property: {
    C: 'general.property.commercial',
    R: 'general.property.residential',
  },
  location: {
    I: 'general.location.indoor',
    O: 'general.location.outdoor',
  },
  groundLevel: {
    A: 'general.groundLevel.above',
    I: 'general.groundLevel.in',
    S: 'general.groundLevel.splasher',
  },
  filterMedia: {
    '': 'general.filterMedia.none',
    'None': 'general.filterMedia.none',
    Sand: 'general.filterMedia.sand',
    DE: 'general.filterMedia.de',
    Cartridge: 'general.filterMedia.cartridge',
    Glass: 'general.filterMedia.glass',
    ZeoBrite: 'general.filterMedia.zeoBrite',
    EcoKlean: 'general.filterMedia.ecoklean',
  },
};

export const spaOptionsTags = {
  finish: {
    A: 'general.finish.acrylic',
    F: 'general.finish.fiberglass',
    P: 'general.finish.plaster',
    C: 'general.finish.plaster.colored',
    W: 'general.finish.tub.wooden',
    T: 'general.finish.plastic.thermal',
    O: 'general.finish.other',
  },
  property: poolOptionsTags.property,
  location: poolOptionsTags.location,
  groundLevel: poolOptionsTags.groundLevel,
  filterMedia: poolOptionsTags.filterMedia,
};

export const swimspaOptionsTags = {
  finish: {
    A: 'general.finish.acrylic',
    F: 'general.finish.fiberglass',
    PL: 'general.finish.plaster',
    V: 'general.finish.vinyl',
  },
  property: poolOptionsTags.property,
  location: poolOptionsTags.location,
  groundLevel: {},
  filterMedia: poolOptionsTags.filterMedia,
};

export const systemOptionsTags = [
  null,
  poolOptionsTags,
  spaOptionsTags,
  swimspaOptionsTags,
];

export const optimizerPlusTitle = (dealer, branch, pool, formatMessage, useNewFormatOnHistory = null) => {
  let translate = undefined !== formatMessage && null !== formatMessage;

  if (pool && pool.typeID === systemTypes.spa) {
    return translate ? formatMessage({id: 'general.optimizer.plus'}) : 'Optimizer Plus';
  }

  if (dealer && dealer.country === countries.US) {
    if(pool && pool.optimizerProductID ===  optimizerProducts.optimizerPlus && useNewFormatOnHistory === false) {
      // Existing pools coming from test history
      return translate ? formatMessage({id: 'general.optimizer.plus'}) : 'Optimizer Plus';
    }

    return translate ? formatMessage({id: 'general.optimizer'}) : 'Optimizer';
  }

  // Canada
  return translate ? formatMessage({id: 'general.optimizer.plus'}) : 'Optimizer Plus';
};

export function fillStars(numStars, plainText = false) {
  let output = [];

  for (let i = 0; i < Math.min(numStars, 5); ++i) {
    output.push(plainText ? '\u2605' : <i className='bl bl-star-filled'
                   key={'star' + i}/>);
  }
  for (let j = Math.min(numStars, 5); j < 5; ++j) {
    output.push(plainText ? '\u2606' : <i className='bl bl-star'
                   key={'emptystar' + j}/>);
  }

  return plainText ? output.join('') : output;
}

export const idealRanges = {
  chemicalBaseTypeID: {
    title: 'System Name',
    displayOptions: (formatMessage, analysis) => {
      if(analysis.chemicalBaseTypeID) {
        return chemicalBaseTypeNames(analysis.poolTypeID || 1, analysis.chemicalBaseTypeID, formatMessage);
      }
      return null;
    },
    required: true,
    separator: true,
  },
  season: {
    title: 'Analysis Type',
    displayOptions: {
      in: 'In Season',
      out: 'Out of Season',
      springopening: 'Spring Opening',
      winterclosing: 'Winter Closing',
    },
    condition: (analysis, pool) => {
      return pool.typeID !== systemTypes.spa;
    },
    separator: true,
  },
  temperature: {
    title: 'Temperature',
    required: true,
    separator: (analysis, pool) => { // Separator if spa since no season
      return pool.typeID === systemTypes.spa;
    },
  },
  saturationIndex: {
    title: 'Saturation Index',
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'saturationIndex', country),
    required: true,
  },
  tDS: {
    title: 'TDS',
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'TDS', country),
    required: true,
  },
  cYA: {
    title: 'CYA',
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'cYA', country),
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        if (pool && pool.chemicalBaseTypeID) {
          if (pool.chemicalBaseTypeID !== chemicalBaseTypes.softswimPool &&
            pool.chemicalBaseTypeID !== chemicalBaseTypes.softSoakSpa) {
            return true;
          }
        }
      }
      return false;
    },
    required: true,
  },
  totalChlorine: {
    title: 'Total Chlorine',
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'totalChlorine', country),
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.softswimPool &&
          sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.softSoakSpa && sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.brominePool &&
          sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.bromineSpa) {
          return true;
        }
      }
      return false;
    },
  },
  freeChlorine: {
    title: 'Free Chlorine',
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'freeChlorine', country),
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.softswimPool &&
          sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.softSoakSpa && sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.brominePool &&
          sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.bromineSpa) {
          return true;
        }
      }
      return false;
    },
  },
  pH: {
    title: 'pH',
    required: true,
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'pH', country),
  },
  alkalinity: {
    title: 'Total Alkalinity',
    required: true,
  },
  adjustedTA: {
    title: 'Adj. Total Alkalinity',
    required: true,
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'adjustedTA', country),
  },
  hardness: {
    title: 'Calcium Hardness',
    required: true,
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'hardness', country),
  },
  initiatorLevel: {
    title: 'Initiator',
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'initiatorLevel', country),
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID &&
           (sanitizer.chemicalBaseTypeID === chemicalBaseTypes.synergyPool || sanitizer.chemicalBaseTypeID === chemicalBaseTypes.synergySpa)) {
          return true;
        }
      }
      return false;
    },
  },
  salt: {
    title: 'Salt',
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'salt', country),
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.name && (sanitizer.chemicalBaseTypeID === chemicalBaseTypes.saltWaterPool ||
            sanitizer.name === 'Saltwater Pool' || sanitizer.chemicalBaseTypeID === chemicalBaseTypes.saltWaterSpa || sanitizer.name === 'Saltwater Spa')) {
          return true;
        }
      }
      return false;
    },
  },
  minerals: {
    title: 'Minerals',
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'minerals', country),
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID === chemicalBaseTypes.mineralSpringsPool) {
          return true;
        }
      }
      return false;
    },
  },
  optimizerPlusBorate: {
    title: optimizerPlusTitle,
    min: 30,
    max: 35,
    required: true,
    condition: (analysis, pool) => {
      if (pool && pool.usingOptimizerPlus) {
        return true;
      }
      return false;
    },
  },
  copper: {
    title: 'Copper',
    required: true,
    ideal: 0,
    min: 0,
    max: 0,
  },
  copperFromProduct: {
    title: 'Copper from Products',
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.softswimPool) {
          return true;
        }
      }
      return false;
    },
  },
  iron: {
    title: 'Iron',
    required: true,
    min: 0,
    max: 0,
  },
  totalBromine: {
    title: 'Total Bromine',
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'totalBromine', country),
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && (sanitizer.chemicalBaseTypeID === chemicalBaseTypes.brominePool ||
            sanitizer.chemicalBaseTypeID === chemicalBaseTypes.bromineSpa)) {
          return true;
        }
      }
      return false;
    },
  },
  sSWB: {
    title: 'SoftSwim B',
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID === chemicalBaseTypes.softswimPool) {
          return true;
        }
      }
      return false;
    },
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'sSWB', country),
  },
  sSWC: {
    title: 'SoftSwim C',
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID === chemicalBaseTypes.softswimPool) {
          return true;
        }
      }
      return false;
    },
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'sSWC', country),
  },
  sSKSanitizerResidual: {
    title: 'SSK Sanitizer',
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID === chemicalBaseTypes.softSoakSpa) {
          return true;
        }
      }
      return false;
    },
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'sSKSanitizerResidual', country),
  },
  sSKShockResidual: {
    title: 'SSK Shock',
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID === chemicalBaseTypes.softSoakSpa) {
          return true;
        }
      }
      return false;
    },
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'sSKShockResidual', country),
  },
  clarifierDemand: {
    title: 'Clarifier Demand',
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && sanitizer.chemicalBaseTypeID === chemicalBaseTypes.softswimPool) {
          return true;
        }
      }
      return false;
    },
  },
  freshFill: {
    title: 'Fresh Fill',
    required: true,
    condition: (analysis, pool, products) => {
      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products && products.length) {
        if (analysis && analysis.season && analysis.season !== seasonTypes.closing) {
          return true;
        }
      }
      return false;
    },
  },
  cleanAndDrain: {
    title: 'Clean And Drain',
    condition: (analysis, pool, products) => {
      let sanitizer = products.find(x => x.id === pool.sanitizer);

      if (sanitizer && sanitizer.chemicalBaseTypeID &&
        (sanitizer.chemicalBaseTypeID === chemicalBaseTypes.chlorineSpa || sanitizer.chemicalBaseTypeID === chemicalBaseTypes.bromineSpa) &&
        pool.softSoakTRIO) {
        return true;
      }
      return false;
    },
    required: true,
  },
  manganese: {
    title: 'Manganese',
    ideal: 'No',
  },
  phosphate: {
    title: 'Phosphate',
    condition: (analysis, pool, products, dealer) => {
      let visible = true;

      if(dealer && dealer.country === countries.US) {
        visible = analysis.season !== seasonTypes.closing;
      } else {
        visible = pool.typeID !== systemTypes.spa && analysis.season !== seasonTypes.closing;
      }

      return visible;
    },
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'phosphate', country),
  },
  quat: {
    title: 'Quat',
    condition: (analysis, pool, products) => {
      let visible = true;

      if (analysis && analysis.id && pool && pool.id && pool.sanitizer && products.length > 0) {
        let sanitizer = products.find(x => x.id === pool.sanitizer);

        if (sanitizer && sanitizer.chemicalBaseTypeID && pool.typeID) {
          visible = sanitizer.chemicalBaseTypeID !== chemicalBaseTypes.softswimPool && pool.typeID !== systemTypes.spa;
        }
      }
      return visible;
    },
    getRange: (country, analysis, pool, products) => getWardenIdealRange(analysis, pool, products, 'quat', country),
    required: true,
  },
  events: {
    title: 'Events',
    condition: (analysis, pool) => {
      return pool.typeID === systemTypes.pool && analysis.season !== seasonTypes.closing && analysis.season !== seasonTypes.outSeason;
    },
    separator: true,
  },
  waterClarity: {
    title: 'Water Clarity',
    ideal: 'Clear',
    required: true,
    separator: true,
  },
  algae: {
    title: 'Algae',
    ideal: 'No',
    required: true,
  },
  surfaceProblems: {
    title: 'Surface Problems',
    ideal: 'No',
    required: true,
  },
  cDSample: {
    title: 'CD Sample (CD Standard)',
  },
  filterCleanedDate: {
    separator: true,
    title: 'Filter Cleaned',
    required: true,
  },
  lastCleanedDrained: {
    separator: true,
    title: 'Drained',
    condition: (analysis, pool) => analysis && pool && pool.typeID === systemTypes.spa,
    required: true,
  },
  waterTester: {
    separator: true,
    required: true,
    title: 'Tester',
  },
  branchName: {
    separator: true,
    required: false,
    condition: (analysis, pool, products) => analysis && analysis.branchID && analysis.branchID > 0,
    title: 'Location',
  },
};

export const idealRangesFormatted = (formatMessage) => {
  if (undefined === formatMessage || null === formatMessage) {
    return idealRanges;
  }

  let list = idealRanges;

  list['chemicalBaseTypeID'].title = formatMessage({id: 'system.details.system.name'});
  list['season'].title = formatMessage({id: 'general.analysis.type'});
  list['temperature'].title = formatMessage({id: 'general.temperature'});
  list['saturationIndex'].title = formatMessage({id: 'general.saturation.index'});
  list['tDS'].title = formatMessage({id: 'general.tds'});
  list['cYA'].title = formatMessage({id: 'analysis.cya'});
  list['totalChlorine'].title = formatMessage({id: 'general.total.chlorine'});
  list['freeChlorine'].title = formatMessage({id: 'general.free.chlorine'});
  list['pH'].title = formatMessage({id: 'general.ph'});
  list['alkalinity'].title = formatMessage({id: 'general.total.alkalinity'});
  list['adjustedTA'].title = formatMessage({id: 'general.adj.total.alkalinity'});
  list['hardness'].title = formatMessage({id: 'general.calcium.hardness'});
  list['salt'].title = formatMessage({id: 'general.salt'});
  list['minerals'].title = formatMessage({id: 'general.minerals'});
  list['copper'].title = formatMessage({id: 'general.copper'});
  list['copperFromProduct'].title = formatMessage({id: 'general.copper.products'});
  list['iron'].title = formatMessage({id: 'general.iron'});
  list['totalBromine'].title = formatMessage({id: 'general.total.bromine'});
  list['sSWB'].title = formatMessage({id: 'general.softswim'}) + ' B';
  list['sSWC'].title = formatMessage({id: 'general.softswim'}) + ' C';
  list['sSKSanitizerResidual'].title = formatMessage({id: 'general.ssk.sanitizer'});
  list['sSKShockResidual'].title = formatMessage({id: 'general.ssk.shock'});
  list['clarifierDemand'].title = formatMessage({id: 'scan.clarifier.demand'});
  list['freshFill'].title = formatMessage({id: 'analysis.fresh.fill'});
  list['cleanAndDrain'].title = formatMessage({id: 'analysis.clean.drain'});
  list['manganese'].title = formatMessage({id: 'general.manganese'});
  list['phosphate'].title = formatMessage({id: 'analysis.phosphate'});
  list['quat'].title = formatMessage({id: 'general.quat'});
  list['events'].title = formatMessage({id: 'general.water.events'});
  list['waterClarity'].title = formatMessage({id: 'general.water.clarity'});
  list['algae'].title = formatMessage({id: 'general.algae'});
  list['surfaceProblems'].title = formatMessage({id: 'general.surface.problems'});
  list['cDSample'].title = formatMessage({id: 'general.cd.sample.standard'});
  list['filterCleanedDate'].title = formatMessage({id: 'general.filter.cleaned'});
  list['lastCleanedDrained'].title = formatMessage({id: 'general.drained'});
  list['waterTester'].title = formatMessage({id: 'general.tester'});
  list['branchName'].title = formatMessage({id: 'general.location'});

  return list;
};

export function getIdealRange(property, analysis, pool, products, country, formatNumber = null) {
  let min, max;

  if (idealRanges && idealRanges[property] && idealRanges[property].hasOwnProperty('min') && idealRanges[property].hasOwnProperty('max')) {
    min = idealRanges[property].min;
    max = idealRanges[property].max;
  } else if (idealRanges && idealRanges[property] && idealRanges[property].getRange) {
    [min, max] = idealRanges[property].getRange(country, analysis, pool, products);
  } else {
    return '';
  }
  let output;

  if (min === -Infinity && max !== Infinity) {
    max = formatNumber ? formatNumber(max) : max;
    output = `< ${max}`;
  } else if (max === Infinity && min !== -Infinity) {
    min = formatNumber ? formatNumber(min) : min;
    output = `> ${min}`;
  } else if (min === max) {
    min = formatNumber ? formatNumber(min) : min;
    output = min || min === 0 ? min.toString() : '';
  } else {
    min = formatNumber ? formatNumber(min) : min;
    max = formatNumber ? formatNumber(max) : max;
    output = `${min} - ${max}`;
  }
  return output;
}

export function getMinMax(property, analysis, pool, products, country) {
  let min, max;

  if (idealRanges && idealRanges[property] && idealRanges[property].min && idealRanges[property].max) {
    min = idealRanges[property].min;
    max = idealRanges[property].max;
  } else if (idealRanges && idealRanges[property] && idealRanges[property].getRange) {
    [min, max] = idealRanges[property].getRange(country, analysis, pool, products);
  }

  if (property === 'totalChlorine') {
    max = Math.min(max, analysis.freeChlorine + 0.399);
  }
  return [min, max];
}

export function getContactName(contact, defaultContactName) {
  if (contact.nameFirst || contact.nameLast) {
    defaultContactName = (contact.nameFirst || '') + ' ' + (contact.nameLast || '');
  } else if (contact.organization) {
    defaultContactName = contact.organization;
  }
  return defaultContactName.trim();
}

export function gallonsToLitres(gal) {
  return gal * 3.78541; // Convert to L (constant from google)
}

export function metersToFeets(mt) {
  return mt / 0.3048; // Convert to Mt (constant from google)
}

export const userRoles = new Map([
  [1000, 'Test Station'],
  [2000, 'Store Admin'],
  [3000, 'Dealer Admin'],
]);

export function canDeleteCustomers(userPrivileges, dealer) {
  if (userPrivileges.level === userLevels.testStation) {
    return dealer.testerDeleteCustomers;
  } else if (userPrivileges.level === userLevels.manager) {
    return dealer.managerDeleteCustomers;
  } else if (userPrivileges.level === userLevels.dealerAdmin) {
    return true;
  }
  return false;
}

export function canDeleteSystems(userPrivileges, dealer) {
  if (userPrivileges.level === userLevels.testStation) {
    return dealer.testerDeleteSystems;
  } else if (userPrivileges.level === userLevels.manager) {
    return dealer.managerDeleteSystems;
  } else if (userPrivileges.level === userLevels.dealerAdmin) {
    return true;
  }
  return false;
}
